import React, { useMemo, useState } from 'react';
import { Shield, ShieldCheck, ShieldAlert, Key, Smartphone, LogOut, AlertTriangle, CheckCircle2, XCircle, Eye, EyeOff, Monitor, Tablet, Bell, Download, Copy, Lock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
interface SecurityTabProps {
  theme: ZodiacTheme;
  user: User | null;
}
const BACKUP_CODES = ['A7K2-M9X1', 'B3P8-N4Q6', 'C5R1-L7W3', 'D9T4-J2Y8', 'E1V6-H5Z0', 'F8U3-G6S7'];
const SESSIONS = [{
  id: 1,
  device: 'Mac OS',
  browser: 'Safari',
  city: 'Москва',
  country: 'Россия',
  ip: '192.168.1.1',
  time: 'Сейчас',
  current: true,
  icon: 'monitor' as const
}, {
  id: 2,
  device: 'iPhone 13',
  browser: 'Safari',
  city: 'Москва',
  country: 'Россия',
  ip: '10.0.0.5',
  time: 'Вчера, 14:30',
  current: false,
  icon: 'phone' as const
}, {
  id: 3,
  device: 'Windows 11',
  browser: 'Chrome',
  city: 'Санкт-Петербург',
  country: 'Россия',
  ip: '85.12.45.67',
  time: '3 дня назад',
  current: false,
  icon: 'monitor' as const
}];
function getPasswordStrength(pw: string): {
  level: number;
  label: string;
  color: string;
} {
  if (!pw) return {
    level: 0,
    label: '',
    color: ''
  };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-zа-я]/.test(pw) && /[A-ZА-Я]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Zа-яА-Я0-9]/.test(pw)) score++;
  if (score <= 1) return {
    level: 1,
    label: 'Слабый',
    color: 'bg-rose-500'
  };
  if (score === 2) return {
    level: 2,
    label: 'Средний',
    color: 'bg-amber-500'
  };
  if (score === 3) return {
    level: 3,
    label: 'Хороший',
    color: 'bg-emerald-400'
  };
  return {
    level: 4,
    label: 'Надёжный',
    color: 'bg-emerald-600'
  };
}
export function SecurityTab({
  theme,
  user
}: SecurityTabProps) {
  const {
    showToast
  } = useToast();
  // Password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  // 2FA
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState<'sms' | 'app' | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  // Sessions
  const [sessions, setSessions] = useState(SESSIONS);
  // Notifications
  const [notifyLogin, setNotifyLogin] = useState(true);
  const [notifyPasswordChange, setNotifyPasswordChange] = useState(true);
  const pwStrength = useMemo(() => getPasswordStrength(newPw), [newPw]);
  const pwChecks = useMemo(() => ({
    length: newPw.length >= 8,
    letters: /[a-zA-Zа-яА-Я]/.test(newPw),
    digits: /\d/.test(newPw),
    match: newPw.length > 0 && newPw === confirmPw
  }), [newPw, confirmPw]);
  // Security score
  const securityScore = useMemo(() => {
    let score = 0;
    const items = [{
      label: 'Пароль обновлён',
      ok: true
    }, {
      label: '2FA включена',
      ok: is2FAEnabled
    }, {
      label: 'Email подтверждён',
      ok: true
    }, {
      label: 'Телефон подтверждён',
      ok: false
    }];
    items.forEach(i => {
      if (i.ok) score++;
    });
    return {
      score,
      total: items.length,
      items
    };
  }, [is2FAEnabled]);
  const scorePercent = securityScore.score / securityScore.total * 100;
  const scoreColor = scorePercent >= 75 ? 'text-emerald-600' : scorePercent >= 50 ? 'text-amber-600' : 'text-rose-600';
  const scoreBg = scorePercent >= 75 ? 'bg-emerald-500' : scorePercent >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  const handleChangePassword = () => {
    if (!currentPw) {
      showToast('Введите текущий пароль');
      return;
    }
    if (!pwChecks.length || !pwChecks.letters || !pwChecks.digits) {
      showToast('Пароль не соответствует требованиям');
      return;
    }
    if (!pwChecks.match) {
      showToast('Пароли не совпадают');
      return;
    }
    showToast('Пароль успешно обновлён');
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
  };
  const handleEnable2FA = (method: 'sms' | 'app') => {
    setTwoFAMethod(method);
    setIs2FAEnabled(true);
    setShow2FASetup(false);
    showToast(`2FA включена через ${method === 'sms' ? 'SMS' : 'Google Authenticator'}`);
  };
  const handleEndSession = (id: number) => {
    setSessions(sessions.filter(s => s.id !== id));
    showToast('Сессия завершена');
  };
  const handleNotMe = (id: number) => {
    setSessions(sessions.filter(s => s.id !== id));
    showToast('Сессия завершена. Рекомендуем сменить пароль');
  };
  const handleEndAllSessions = () => {
    setSessions(sessions.filter(s => s.current));
    showToast('Выполнен выход со всех других устройств');
  };
  const Toggle = ({
    checked,
    onChange
  }: {
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => <button onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-19087">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-19088" />
    </button>;
  const DeviceIcon = ({
    type
  }: {
    type: 'monitor' | 'phone';
  }) => type === 'monitor' ? <Monitor className="w-4 h-4" data-id="element-19089" /> : <Smartphone className="w-4 h-4" data-id="element-19090" />;
  return <div className="space-y-5 max-w-3xl pb-8" data-id="element-19091">
      {/* Header */}
      <div data-id="element-19092">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19093">
          Безопасность
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-19094">
          Контроль и защита вашего аккаунта
        </p>
      </div>

      {/* Security Score */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm" data-id="element-19095">
        <div className="flex items-center justify-between mb-3" data-id="element-19096">
          <div className="flex items-center gap-2" data-id="element-19097">
            {scorePercent >= 75 ? <ShieldCheck className="w-5 h-5 text-emerald-600" data-id="element-19098" /> : <ShieldAlert className="w-5 h-5 text-amber-500" data-id="element-19099" />}
            <h3 className="font-bold text-slate-900 text-sm" data-id="element-19100">
              Статус безопасности
            </h3>
          </div>
          <span className={`text-lg font-bold ${scoreColor}`} data-id="element-19101">
            {securityScore.score}/{securityScore.total}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 mb-3" data-id="element-19102">
          <div className={`h-2 rounded-full transition-all ${scoreBg}`} style={{
          width: `${scorePercent}%`
        }} data-id="element-19103" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-19104">
          {securityScore.items.map((item, i) => <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${item.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-19105">
              {item.ok ? <CheckCircle2 className="w-3 h-3 shrink-0" data-id="element-19106" /> : <XCircle className="w-3 h-3 shrink-0" data-id="element-19107" />}
              <span className="truncate" data-id="element-19108">{item.label}</span>
            </div>)}
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19109">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19110">
          <Key className="w-4 h-4 text-slate-400" data-id="element-19111" /> Изменение пароля
        </h3>

        <div data-id="element-19112">
          <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19113">
            Текущий пароль
          </label>
          <div className="relative" data-id="element-19114">
            <input type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19115" />
            <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-19116">
              {showCurrentPw ? <EyeOff className="w-4 h-4" data-id="element-19117" /> : <Eye className="w-4 h-4" data-id="element-19118" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-19119">
          <div data-id="element-19120">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19121">
              Новый пароль
            </label>
            <div className="relative" data-id="element-19122">
              <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19123" />
              <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-19124">
                {showNewPw ? <EyeOff className="w-4 h-4" data-id="element-19125" /> : <Eye className="w-4 h-4" data-id="element-19126" />}
              </button>
            </div>
          </div>
          <div data-id="element-19127">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19128">
              Повторите пароль
            </label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19129" />
          </div>
        </div>

        {/* Strength indicator */}
        {newPw && <div className="space-y-2" data-id="element-19130">
            <div className="flex items-center gap-2" data-id="element-19131">
              <div className="flex gap-1 flex-1" data-id="element-19132">
                {[1, 2, 3, 4].map(lvl => <div key={lvl} className={`h-1.5 flex-1 rounded-full transition-colors ${lvl <= pwStrength.level ? pwStrength.color : 'bg-slate-100'}`} data-id="element-19133" />)}
              </div>
              <span className={`text-[10px] font-medium ${pwStrength.level <= 1 ? 'text-rose-600' : pwStrength.level === 2 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-19134">
                {pwStrength.label}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-19135">
              {[{
            ok: pwChecks.length,
            label: '8+ символов'
          }, {
            ok: pwChecks.letters,
            label: 'Буквы'
          }, {
            ok: pwChecks.digits,
            label: 'Цифры'
          }, {
            ok: pwChecks.match,
            label: 'Совпадают'
          }].map((c, i) => <span key={i} className={`text-[10px] flex items-center gap-0.5 ${c.ok ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-19136">
                  {c.ok ? <CheckCircle2 className="w-3 h-3" data-id="element-19137" /> : <XCircle className="w-3 h-3" data-id="element-19138" />}{' '}
                  {c.label}
                </span>)}
            </div>
          </div>}

        <Button size="sm" className="text-xs" onClick={handleChangePassword} disabled={!currentPw || !pwChecks.length || !pwChecks.digits || !pwChecks.match} data-id="element-19139">
          Обновить пароль
        </Button>
      </div>

      {/* 2FA */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19140">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19141">
          <Shield className="w-4 h-4 text-slate-400" data-id="element-19142" /> Двухфакторная
          аутентификация
        </h3>

        <div className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${is2FAEnabled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-id="element-19143">
          <div data-id="element-19144">
            <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-19145">
              {is2FAEnabled ? 'Включена' : 'Выключена'}
              {is2FAEnabled && twoFAMethod && <Badge variant="success" size="sm" className="text-[9px] py-0 h-4" data-id="element-19146">
                  {twoFAMethod === 'sms' ? 'SMS' : 'Authenticator'}
                </Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-id="element-19147">
              Дополнительный уровень защиты при входе
            </div>
          </div>
          {is2FAEnabled ? <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => {
          setIs2FAEnabled(false);
          setTwoFAMethod(null);
          showToast('2FA отключена');
        }} data-id="element-19148">
              Отключить
            </Button> : <Button size="sm" className="text-xs shrink-0" onClick={() => setShow2FASetup(true)} data-id="element-19149">
              Включить
            </Button>}
        </div>

        {/* 2FA Setup */}
        {show2FASetup && !is2FAEnabled && <div className="space-y-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200" data-id="element-19150">
            <div className="text-xs font-medium text-indigo-900" data-id="element-19151">
              Выберите способ:
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-19152">
              <button onClick={() => handleEnable2FA('sms')} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors text-left" data-id="element-19153">
                <Smartphone className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-19154" />
                <div data-id="element-19155">
                  <div className="text-xs font-medium text-slate-900" data-id="element-19156">SMS</div>
                  <div className="text-[10px] text-slate-500" data-id="element-19157">
                    Код на телефон
                  </div>
                </div>
              </button>
              <button onClick={() => handleEnable2FA('app')} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors text-left" data-id="element-19158">
                <Lock className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-19159" />
                <div data-id="element-19160">
                  <div className="text-xs font-medium text-slate-900" data-id="element-19161">
                    Authenticator
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-19162">
                    Google / Authy
                  </div>
                </div>
              </button>
            </div>
          </div>}

        {/* Backup Codes */}
        {is2FAEnabled && <div data-id="element-19163">
            <button onClick={() => setShowBackupCodes(!showBackupCodes)} className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1" data-id="element-19164">
              <Key className="w-3 h-3" data-id="element-19165" />{' '}
              {showBackupCodes ? 'Скрыть' : 'Показать'} резервные коды
            </button>
            {showBackupCodes && <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-id="element-19166">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-id="element-19167">
                  {BACKUP_CODES.map(code => <div key={code} className="text-xs font-mono text-slate-700 bg-white px-2 py-1.5 rounded border border-slate-200 text-center" data-id="element-19168">
                      {code}
                    </div>)}
                </div>
                <div className="flex gap-2" data-id="element-19169">
                  <button onClick={() => {
              navigator.clipboard?.writeText(BACKUP_CODES.join('\n'));
              showToast('Коды скопированы');
            }} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-19170">
                    <Copy className="w-3 h-3" data-id="element-19171" /> Копировать
                  </button>
                  <button onClick={() => showToast('Коды скачаны')} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-19172">
                    <Download className="w-3 h-3" data-id="element-19173" /> Скачать
                  </button>
                </div>
              </div>}
          </div>}
      </div>

      {/* Active Sessions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19174">
        <div className="flex items-center justify-between" data-id="element-19175">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19176">
            <Monitor className="w-4 h-4 text-slate-400" data-id="element-19177" /> Активные сессии
          </h3>
          {sessions.filter(s => !s.current).length > 0 && <Button variant="outline" size="sm" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50 font-medium" onClick={handleEndAllSessions} data-id="element-19178">
              <LogOut className="w-3.5 h-3.5 mr-1" data-id="element-19179" /> Выйти со всех
            </Button>}
        </div>

        <div className="space-y-2" data-id="element-19180">
          {sessions.map(s => <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border ${s.current ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200'}`} data-id="element-19181">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.current ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-19182">
                <DeviceIcon type={s.icon} data-id="element-19183" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-19184">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-19185">
                  {s.device} · {s.browser}
                  {s.current && <Badge variant="success" size="sm" className="text-[9px] py-0 h-4" data-id="element-19186">
                      Текущая
                    </Badge>}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19187">
                  {s.city}, {s.country} · IP: {s.ip} · {s.time}
                </div>
              </div>
              {!s.current && <div className="flex items-center gap-1 shrink-0" data-id="element-19188">
                  <button onClick={() => handleEndSession(s.id)} className="text-[10px] text-slate-500 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50 transition-colors" data-id="element-19189">
                    Завершить
                  </button>
                  <button onClick={() => handleNotMe(s.id)} className="text-[10px] text-rose-600 hover:text-rose-700 px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 font-medium transition-colors" data-id="element-19190">
                    Это не я
                  </button>
                </div>}
            </div>)}
        </div>
      </div>

      {/* Security Notifications */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-3" data-id="element-19191">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19192">
          <Bell className="w-4 h-4 text-slate-400" data-id="element-19193" /> Уведомления безопасности
        </h3>
        <div className="divide-y divide-slate-100" data-id="element-19194">
          <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-19195">
            <div data-id="element-19196">
              <div className="text-sm font-medium text-slate-900" data-id="element-19197">
                Вход в аккаунт
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-19198">
                Email при каждом входе с нового устройства
              </div>
            </div>
            <Toggle checked={notifyLogin} onChange={setNotifyLogin} data-id="element-19199" />
          </div>
          <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-19200">
            <div data-id="element-19201">
              <div className="text-sm font-medium text-slate-900" data-id="element-19202">
                Смена пароля
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-19203">
                Уведомление при изменении пароля
              </div>
            </div>
            <Toggle checked={notifyPasswordChange} onChange={setNotifyPasswordChange} data-id="element-19204" />
          </div>
        </div>
      </div>
    </div>;
}